import React, { useState } from "react";
import { Button } from "./ui/Button";
import VideoEmbedModal from "./VideoEmbedModal";

export interface DynamicButtonLinkProps {
  buttonText?: string;
  buttonLink?: string;
  buttonTarget?: string;
  variant?:
    | "primary"
    | "secondary"
    | "tertiary"
    | "success"
    | "error"
    | "warning"
    | "info"
    | "link"
    | "outline";
  size?: "sm" | "md" | "lg";
  fullWidth?: boolean;
  className?: string;
  isLoading?: boolean;
  loadingText?: string;
  icon?: React.ReactNode;
}

/**
 * DynamicButtonLink component
 *
 * A reusable button component that can be rendered as either a NavLink or an anchor
 * based on the buttonTarget prop.
 *
 * @example
 * // As an internal link (NavLink)
 * <DynamicButtonLink buttonText="Go to Dashboard" buttonLink="/dashboard" buttonTarget="internal" />
 *
 * // As an external link
 * <DynamicButtonLink buttonText="Visit Website" buttonLink="https://example.com" buttonTarget="_blank" />
 */
const DynamicButtonLink: React.FC<DynamicButtonLinkProps> = ({
  buttonText = "",
  buttonLink = "",
  buttonTarget = "_self",
  variant = "primary",
  size = "lg",
  fullWidth = false,
  className = "",
  isLoading = false,
  loadingText,
  icon,
}) => {
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  // Handle video embed modal
  if (buttonTarget === "_embed") {
    return (
      <>
        <Button
          variant={variant}
          size={size}
          fullWidth={fullWidth}
          className={className}
          isLoading={isLoading}
          loadingText={loadingText}
          onClick={() => setIsVideoModalOpen(true)}
        >
          <span>{buttonText}</span>
          {icon}
        </Button>
        
        {/* Video Embed Modal */}
        <VideoEmbedModal
          isOpen={isVideoModalOpen}
          onClose={() => setIsVideoModalOpen(false)}
          videoUrl={buttonLink}
        />
      </>
    );
  }
  
  // Regular internal link
  if (buttonTarget === "internal") {
    return (
      <Button
        as="navlink"
        to={buttonLink}
        variant={variant}
        size={size}
        fullWidth={fullWidth}
        className={className}
        isLoading={isLoading}
        loadingText={loadingText}
      >
        <span>{buttonText}</span>
        {icon}
      </Button>
    );
  }
  
  // External link
  return (
    <Button
      as="a"
      href={buttonLink}
      variant={variant}
      size={size}
      fullWidth={fullWidth}
      className={className}
      target={buttonTarget}
      isLoading={isLoading}
      loadingText={loadingText}
    >
      <span>{buttonText}</span>
      {icon}
    </Button>
  );
};

export default DynamicButtonLink;
